import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { BiddingApprovalState } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type {
  BiddingSpotApprovalRequest,
  BiddingSpotBid,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'

import { isNonExpiredDraftBlockRow } from './blockDraftGroup'

/**
 * 入札全削除 (機能詳細 7 / #7758) の実行手順の 1 件。 申請セット単位。
 *
 * - `delete`: 申請セットの札が全部削除対象。 申請セットごと消す (`Update` の札は 1 件以上
 *   必要なので、 札が 0 件になる時は `Delete`)
 * - `keep`: 札の一部だけが削除対象。 残す札 (`remaining`) で申請セットを更新する
 */
export type ClearAllStep =
  | { kind: 'delete'; request: BiddingSpotApprovalRequest }
  | {
      kind: 'keep'
      request: BiddingSpotApprovalRequest
      remaining: BiddingSpotBid[]
    }

/**
 * 下書きの申請セットのうち、 `targetBidIds` の札を消す手順を組む。
 *
 * 削除対象は入札一覧に表示中の下書きだけ (表示していない札は消さない)。 1 つの申請セットに
 * 表示中の札と表示外の札が混ざるので、 札単位で消す。 対象を含まない申請セットと、
 * 下書き以外の申請セットは手順に含めない。
 */
export const planClearAll = (
  requests: readonly BiddingSpotApprovalRequest[],
  targetBidIds: ReadonlySet<string>,
): ClearAllStep[] =>
  requests.flatMap((request): ClearAllStep[] => {
    if (request.approvalState !== BiddingApprovalState.DRAFT) return []
    const remaining = request.spotBids.filter((b) => !targetBidIds.has(b.id))
    if (remaining.length === request.spotBids.length) return []
    return remaining.length === 0
      ? [{ kind: 'delete', request }]
      : [{ kind: 'keep', request, remaining }]
  })

/**
 * 手順が消す札の数。 確認 modal に出した札の数と一致しない時は、 modal を開いた後に
 * 申請セットが更新された (札が増減した・下書きでなくなった) ので、 書き込まずに中断する。
 */
export const countClearedBids = (steps: readonly ClearAllStep[]): number =>
  steps.reduce(
    (sum, step) =>
      sum +
      step.request.spotBids.length -
      (step.kind === 'keep' ? step.remaining.length : 0),
    0,
  )

/**
 * ブロック入札の全削除 (機能詳細 7 / #7758) で消す group を、 一覧に表示中の leg から決める。
 *
 * ブロックの group は leg 単体を消せないので、 表示中の leg を持つ下書きの group を、 全 leg ごと
 * 対象にする。 入札締切まで提出せず失効にした group は下書きに数えない (AC-BSP-099)。
 * 一覧はエリアの絞り込みでも LINK / LOOP の組を分けずに表示する
 * (`filterBlockRowsByAreas`) ので、 表示中の group は全 leg が表示されている。
 * `rows` の並びのまま、 対象 group の全 leg を返す。
 */
export const resolveBlockClearAllScope = <
  R extends {
    id: string
    groupId: string
    approvalState: BiddingApprovalState
    groupExpired: boolean
  },
>(
  rows: readonly R[],
  visibleRows: readonly R[],
): R[] => {
  const visibleLegIds = new Set(visibleRows.map((r) => r.id))
  const targetGroupIds = new Set(
    rows
      .filter((r) => isNonExpiredDraftBlockRow(r) && visibleLegIds.has(r.id))
      .map((r) => r.groupId),
  )
  return rows.filter((r) => targetGroupIds.has(r.groupId))
}

/**
 * ブロック入札の全削除 (機能詳細 7 / #7758) で消す group を、 確認 modal を開いた時点の
 * 更新日時 (`expectedUpdatedAt`: group ID → 更新日時) と照合して決める。
 *
 * ブロックの group は編集しても ID と下書きの状態が変わらないので、 ID だけの照合では
 * modal を開いた後に他のユーザーが編集した group を見逃し、 modal に出した内容と違う group を消して
 * しまう。 modal に出した group がすべて下書きに残っていて、 更新日時が開いた時点から
 * 動いていない時だけ、 その group を返す。 1 件でも欠けた・動いた時は `undefined` を返し、
 * 呼び出し側は書き込まずに中断する。
 */
export const resolveBlockClearAllGroups = <
  G extends {
    id: string
    approvalState: BiddingApprovalState
    updatedAt?: Timestamp
  },
>(
  groups: readonly G[],
  expectedUpdatedAt: ReadonlyMap<string, Timestamp | undefined>,
): G[] | undefined => {
  const drafts = groups.filter(
    (g) => g.approvalState === BiddingApprovalState.DRAFT,
  )
  const targets = drafts.filter((g) => expectedUpdatedAt.has(g.id))
  if (targets.length !== expectedUpdatedAt.size) return undefined
  const isUnchanged = targets.every((g) => {
    const expected = expectedUpdatedAt.get(g.id)
    return (
      g.updatedAt?.seconds === expected?.seconds &&
      g.updatedAt?.nanos === expected?.nanos
    )
  })
  return isUnchanged ? targets : undefined
}
